import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation, useQuery } from 'convex/react'
import { useState } from 'react'
import { toast } from 'sonner'

import { UserAvatar } from '@/components/user-avatar'

// Before/after stories whose authors asked to be featured on goaltree.ai.
// Approving grants the author a free Pro month (once).
export function AdminJourneyFeatures() {
  const rows = useQuery(api.journeys.featureQueueForAdmin)
  const review = useMutation(api.journeys.reviewFeature)
  const [busy, setBusy] = useState<string | null>(null)
  if (!rows) return null
  const pending = rows.filter((r) => r.status === 'pending').length

  const act = async (journeyId: Id<'journeys'>, approve: boolean) => {
    setBusy(journeyId)
    try {
      await review({ journeyId, approve })
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not update')
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-xs font-semibold uppercase tracking-widest text-foreground/40">
        Stories to feature · {pending} to review
      </h2>
      {rows.length === 0 && <p className="text-sm text-muted-foreground">No requests.</p>}
      {rows.map((r) => (
        <div
          key={r.id}
          className={cn(
            'flex flex-col gap-2 rounded-xl border border-border/60 bg-card/40 p-3',
            r.status !== 'pending' && 'opacity-60',
          )}
        >
          <div className="flex items-center gap-2">
            <UserAvatar name={r.name} image={r.image} className="size-6" />
            <span className="truncate text-sm font-semibold text-foreground">{r.name}</span>
            <span className="truncate text-xs text-muted-foreground">· {r.goalTitle}</span>
            <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">
              {new Date(r.requestedAt).toLocaleDateString()}
            </span>
          </div>
          <div className="flex gap-2 overflow-x-auto">
            {r.entries.map((e) => (
              <div key={e.id} className="flex w-28 shrink-0 flex-col gap-1">
                <div className="aspect-[4/5] overflow-hidden rounded-lg bg-muted">
                  {e.photoUrl ? (
                    <a href={e.photoUrl} target="_blank" rel="noreferrer">
                      <img src={e.photoUrl} alt="" className="size-full object-cover" />
                    </a>
                  ) : (
                    <div className="flex size-full items-center justify-center text-[11px] text-muted-foreground">
                      No photo
                    </div>
                  )}
                </div>
                <span className="text-[11px] font-semibold text-muted-foreground">
                  {e.kind === 'milestone' ? e.milestoneTitle : e.kind}
                  {e.photoStatus ? ` · photo ${e.photoStatus}` : ''}
                </span>
                {e.note && <p className="line-clamp-4 text-xs text-foreground/80">“{e.note}”</p>}
              </div>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground">
            Only photos marked ok show publicly. Check notes for health or medical claims before
            approving.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <a
              href={r.publicUrl}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-semibold text-primary-text underline-offset-2 hover:underline"
            >
              Public page
            </a>
            {r.status === 'pending' ? (
              <>
                <button
                  type="button"
                  disabled={busy === r.id || !r.consent || !r.isPublic}
                  onClick={() => void act(r.id, true)}
                  className="ml-auto cursor-pointer rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
                >
                  Approve · grant Pro month
                </button>
                <button
                  type="button"
                  disabled={busy === r.id}
                  onClick={() => void act(r.id, false)}
                  className="cursor-pointer rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
                >
                  Reject
                </button>
              </>
            ) : (
              <span className="ml-auto text-xs font-semibold text-muted-foreground">
                {r.status === 'approved' ? 'Approved' : 'Rejected'}
                {r.reviewedAt ? ` · ${new Date(r.reviewedAt).toLocaleDateString()}` : ''}
                {r.rewarded ? ' · Pro month granted' : ''}
                {r.status === 'approved' && (!r.consent || !r.isPublic) ? ' · withdrawn' : ''}
              </span>
            )}
          </div>
        </div>
      ))}
    </section>
  )
}
